/**
 * TipTap editor — spec §8.4 `RichTextEditor`.
 *
 * One component for note bodies, task descriptions and comments. The three
 * differ only in toolbar and autosave, so they pass props rather than forking.
 *
 * `#VL-142` autolinks to the task. That is done with a paste/input rule here
 * and rendered as a link, so the same text round-trips through the server's
 * sanitiser (`lib/richText.ts`) unchanged.
 */

import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import {
  Bold,
  Code,
  Heading2,
  Italic,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Quote,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface RichTextEditorHandle {
  /** The current document, ready to send to the API. */
  getJSON: () => Record<string, unknown>;
  /** Plain text of the current selection — used by convert-to-task. */
  getSelectionText: () => string;
  focus: () => void;
  /** V4 F-2 — drops an uploaded image in at the caret. */
  insertImage: (src: string) => void;
  editor: Editor | null;
}

interface Props {
  value?: Record<string, unknown> | null;
  onChange?: (doc: Record<string, unknown>) => void;
  onSubmit?: () => void;
  placeholder?: string;
  editable?: boolean;
  minHeight?: number;
  toolbar?: boolean;
  className?: string;
  handleRef?: Ref<RichTextEditorHandle>;
  'data-testid'?: string;
  /** V4 F-2 — receives pasted image files; the caller uploads and inserts. */
  onPasteImage?: (files: File[]) => Promise<void> | void;
}

const TASK_KEY = /(^|\s)(#[A-Z][A-Z0-9]{1,4}-\d+)$/;

function ToolbarButton({
  active,
  onClick,
  label,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep the selection
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={cn(
        'grid size-7 place-items-center rounded-[4px] transition-colors duration-[120ms]',
        active
          ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
          : 'text-[var(--text-muted)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]',
      )}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({
  value,
  onChange,
  onSubmit,
  placeholder = 'Write something…',
  editable = true,
  minHeight = 120,
  toolbar = true,
  className,
  handleRef,
  onPasteImage,
  'data-testid': testId,
}: Props) {
  const latest = useRef(value);

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({ inline: false }),
      Placeholder.configure({ placeholder }),
      TaskList,
      TaskItem.configure({ nested: false }),
    ],
    content: (value as never) ?? undefined,
    editorProps: {
      attributes: {
        class: 'vt-prose focus:outline-none',
        style: `min-height:${minHeight}px`,
        ...(testId ? { 'data-testid': testId } : {}),
      },
      /**
       * V4 F-2 — paste an image straight into the note.
       *
       * Screenshots are the single most common thing anyone pastes into a
       * document, and until now they were silently dropped. The bytes go up
       * through the normal presign flow, so quota and scanning apply exactly
       * as they do to a chosen file.
       */
      handlePaste: (_view, event) => {
        if (!onPasteImage) return false;
        const files = Array.from(event.clipboardData?.files ?? []).filter((f) =>
          f.type.startsWith('image/'),
        );
        if (files.length === 0) return false;
        event.preventDefault();
        void onPasteImage(files);
        return true;
      },
      handleKeyDown: (_view, event) => {
        if (onSubmit && (event.metaKey || event.ctrlKey) && event.key === 'Enter') {
          event.preventDefault();
          onSubmit();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => {
      const doc = e.getJSON() as Record<string, unknown>;
      latest.current = doc;
      onChange?.(doc);
    },
  });

  // `#VL-142` → link, applied as you type past the key.
  useEffect(() => {
    if (!editor) return;
    const handler = (): void => {
      const { state } = editor;
      const { from } = state.selection;
      const textBefore = state.doc.textBetween(Math.max(0, from - 40), from, '\n', ' ');
      const match = TASK_KEY.exec(textBefore);
      if (!match) return;
      const key = match[2].slice(1);
      const start = from - match[2].length;
      editor
        .chain()
        .setTextSelection({ from: start, to: from })
        .setLink({ href: `/tasks/${key}` })
        .setTextSelection(from)
        .run();
    };
    editor.on('update', handler);
    return () => {
      editor.off('update', handler);
    };
  }, [editor]);

  // Re-hydrate when the loaded document arrives or the caller swaps notes.
  useEffect(() => {
    if (!editor || !value) return;
    if (JSON.stringify(value) === JSON.stringify(latest.current)) return;
    latest.current = value;
    // The `false` is TipTap 2's emitUpdate flag: re-hydrating must not look
    // like the user typed, or it would trigger another autosave.
    editor.commands.setContent(value as never, false);
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  useImperativeHandle(
    handleRef,
    () => ({
      getJSON: () => (editor?.getJSON() as Record<string, unknown>) ?? { type: 'doc', content: [] },
      getSelectionText: () => {
        if (!editor) return '';
        const { from, to } = editor.state.selection;
        return editor.state.doc.textBetween(from, to, '\n').trim();
      },
      focus: () => editor?.commands.focus(),
      insertImage: (src: string) => editor?.chain().focus().setImage({ src }).run(),
      editor,
    }),
    [editor],
  );

  if (!editor) return <div style={{ minHeight }} aria-busy="true" />;

  return (
    <div className={cn('rounded-[6px] border border-[var(--border)] bg-[var(--surface)]', className)}>
      {toolbar && editable && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-[var(--border)] px-1.5 py-1">
          <ToolbarButton
            label="Bold"
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            label="Heading"
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <Heading2 className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            label="Bullet list"
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            label="Numbered list"
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            label="Checklist"
            active={editor.isActive('taskList')}
            onClick={() => editor.chain().focus().toggleTaskList().run()}
          >
            <ListChecks className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            label="Quote"
            active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <Quote className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            label="Code block"
            active={editor.isActive('codeBlock')}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <Code className="size-3.5" />
          </ToolbarButton>
          <ToolbarButton
            label="Link"
            active={editor.isActive('link')}
            onClick={() => {
              const href = window.prompt('Link URL');
              if (href === null) return;
              if (href === '') editor.chain().focus().unsetLink().run();
              else editor.chain().focus().setLink({ href }).run();
            }}
          >
            <Link2 className="size-3.5" />
          </ToolbarButton>
        </div>
      )}
      <div className="px-3 py-2">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
